/**
 * Universal authentication provider that supports both OIDC and iframe modes
 * Automatically switches based on VITE_IFRAME_MODE environment variable
 */

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

import type { Session, User } from "./auth-types";
import { IframeAuthProvider } from "./iframe-auth-provider";
import { isIframeMode } from "./iframe-auth-utils";

export interface AuthContextValue {
  session: Session | null;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  isAuthenticated: boolean;
  signIn: () => void;
  signOut: () => void;
  refreshSession: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  signInPath?: string;
  signOutPath?: string;
  sessionCheckPath?: string;
}

/**
 * OIDC Authentication Provider (internal)
 * Used when not in iframe mode
 */
function OidcAuthProvider({
  children,
  signInPath = "/auth/login",
  signOutPath = "/auth/logout",
  sessionCheckPath = "/api/auth/me",
}: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshSession = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(sessionCheckPath, {
        credentials: "include",
        headers: {
          "ngrok-skip-browser-warning": "69420",
        },
        redirect: "manual", // Don't follow redirects automatically
      });

      if (response.ok) {
        const data = (await response.json()) as { user: User };
        setSession({
          authenticated: true,
          user: data.user,
        });
      } else if (
        response.status === 401 ||
        response.type === "opaqueredirect" ||
        response.status === 0
      ) {
        // User is not authenticated
        setSession({
          authenticated: false,
          user: null,
        });
      } else {
        throw new Error(`Failed to check session: ${response.status}`);
      }
    } catch {
      // If fetch fails, assume user is not authenticated
      setSession({
        authenticated: false,
        user: null,
      });
      setError(null); // Don't show error for auth check failures
    } finally {
      setIsLoading(false);
    }
  }, [sessionCheckPath]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const signIn = useCallback(() => {
    window.location.href = signInPath;
  }, [signInPath]);

  const signOut = useCallback(() => {
    window.location.href = signOutPath;
  }, [signOutPath]);

  const contextValue: AuthContextValue = {
    session,
    isLoading,
    error,
    user: session?.user || null,
    isAuthenticated: session?.authenticated || false,
    signIn,
    signOut,
    refreshSession,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

/**
 * Universal Authentication Provider
 * Automatically switches between OIDC and iframe modes based on environment
 */
export function AuthProvider(props: AuthProviderProps) {
  // Check if we should use iframe mode
  if (isIframeMode()) {
    return <IframeAuthProvider {...props} />;
  }

  // Default to OIDC mode
  return <OidcAuthProvider {...props} />;
}

/**
 * Hook to access auth context (works for both OIDC and iframe modes)
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
